/**
 * Normaliza email y token del POST /api/admin/admin-login
 * (distintas formas: result.token, accessToken, JWT anidado, etc.)
 */

import axios from "axios";

function pickString(v) {
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s || s === "undefined" || s === "null") return null;
  return s;
}

function looksLikeJwt(s) {
  if (typeof s !== "string") return false;
  const parts = s.split(".");
  if (parts.length !== 3) return false;
  return parts.every((p) => p.length > 0);
}

function findJwtDeep(obj, depth = 0) {
  if (depth > 10 || obj == null) return null;
  if (typeof obj === "string") {
    return looksLikeJwt(obj) ? obj : null;
  }
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      const nested = findJwtDeep(obj[i], depth + 1);
      if (nested) return nested;
    }
    return null;
  }
  if (typeof obj !== "object") return null;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (typeof v === "string" && looksLikeJwt(v)) return v;
    const nested = findJwtDeep(v, depth + 1);
    if (nested) return nested;
  }
  return null;
}

function normalizeToken(raw) {
  let t = pickString(raw);
  if (!t) return null;
  if (t.toLowerCase().startsWith("bearer ")) {
    t = t.slice(7).trim();
  }
  return t || null;
}

/**
 * @param {import('axios').AxiosResponse} res
 * @param {string} [emailHint] — email del formulario si el API no lo devuelve
 */
export function extractAdminLoginSession(res, emailHint = null) {
  const d = res?.data;
  if (!d) return null;

  const r = d.result ?? d.data;
  const block = r && typeof r === "object" && !Array.isArray(r) ? r : d;

  let token =
    normalizeToken(block?.token) ||
    normalizeToken(block?.accessToken) ||
    normalizeToken(block?.jwt) ||
    normalizeToken(d.token) ||
    normalizeToken(d.accessToken) ||
    (typeof d.result === "string" ? normalizeToken(d.result) : null);

  if (!token) {
    token = findJwtDeep(d);
  }

  let email =
    pickString(block?.email) ||
    pickString(block?.userEmail) ||
    pickString(block?.user?.email) ||
    pickString(d?.user?.email) ||
    pickString(d.email) ||
    (typeof emailHint === "string" ? pickString(emailHint) : null);

  if (!token) return null;

  if (!email || !email.includes("@")) {
    if (typeof emailHint === "string" && pickString(emailHint)?.includes("@")) {
      email = pickString(emailHint);
    } else {
      return null;
    }
  }

  return { token, email };
}

export function isStoredTokenValid() {
  const token = window.sessionStorage.getItem("Token");
  const user = window.sessionStorage.getItem("User");
  if (!token || !user) return false;
  if (token === "undefined" || user === "undefined") return false;
  if (!user.includes("@")) return false;
  return true;
}

/** Cabeceras que suelen aceptar los APIs ACV (x-access-token y/o Bearer). */
export function getAuthHeaders() {
  const token = window.sessionStorage.getItem("Token");
  if (!token || token === "undefined") return {};
  return {
    "x-access-token": token,
    Authorization: `Bearer ${token}`,
  };
}

let axiosAuthInterceptorInstalled = false;

function resolveAbsoluteUrl(config) {
  const raw = config.url || "";
  if (raw.startsWith("http")) return raw;
  const base = (config.baseURL || process.env.REACT_APP_BASEURL || "").replace(
    /\/$/,
    ""
  );
  if (!base) return raw;
  if (!raw) return base;
  return `${base}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

/**
 * Interceptor: adjunta auth a todas las peticiones al REACT_APP_BASEURL salvo login.
 * Así no depende de que cada pantalla recuerde las cabeceras.
 */
export function setupAxiosAuth() {
  if (axiosAuthInterceptorInstalled) return;
  axiosAuthInterceptorInstalled = true;

  const base = process.env.REACT_APP_BASEURL;
  if (!base) return;

  const normalizedBase = base.replace(/\/$/, "");
  const hostOnly = normalizedBase.replace(/^https?:\/\//, "");

  axios.interceptors.request.use((config) => {
    const absolute = resolveAbsoluteUrl(config);
    if (!absolute || absolute.includes("/admin-login")) {
      return config;
    }
    const isOurApi =
      absolute.startsWith(normalizedBase) ||
      absolute.includes(hostOnly);
    if (!isOurApi) {
      return config;
    }

    const token = window.sessionStorage.getItem("Token");
    if (!token || token === "undefined") {
      return config;
    }

    config.headers = config.headers || {};
    if (!config.headers["x-access-token"]) {
      config.headers["x-access-token"] = token;
    }
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}
